/**
 * Tenant credit-wallet query hooks (credit-wallet Phase 5a).
 *
 * The smart data layer behind the `/usage` credits page. Each hook leans on
 * {@link useCompanyQuery}, which resolves the active company, supplies the
 * org-id cache segment, and guards on the client — the same convention as every
 * other tenant hook, so we intentionally do NOT take a `companyId` argument that
 * could drift from the active org. Everything returned is CREDITS ONLY (the
 * backend dropped every internal money figure at the wire).
 */

import type { UseQueryResult } from "@tanstack/react-query"
import {
  fetchCreditStatement,
  fetchCreditTransactions,
  fetchCreditWallet,
  type ICreditTransactionsPage,
  type ITenantCreditStatement,
  type ITenantCreditWallet,
} from "../lib/api/credits.ts"
import { queryKeys } from "../lib/query-keys.ts"
import { useCompanyQuery } from "./useCompanyQuery.ts"

/** Live wallet balance — spendable credits, grant/pack split, projected run-out. */
export function useCreditWallet(): UseQueryResult<ITenantCreditWallet> {
  return useCompanyQuery({
    key: queryKeys.creditWallet,
    queryFn: (company) => fetchCreditWallet(company),
  })
}

/** One page of the credit ledger. `limit`/`offset` are part of the cache key. */
export function useCreditTransactions({
  limit = 50,
  offset = 0,
}: {
  limit?: number
  offset?: number
}): UseQueryResult<ICreditTransactionsPage> {
  return useCompanyQuery({
    key: (orgId) => queryKeys.creditTransactions(orgId, limit, offset),
    queryFn: (company) => fetchCreditTransactions(company, limit, offset),
  })
}

/** Credit consumption statement over an inclusive `from`/`to` day window. */
export function useCreditStatement({ from, to }: { from: string; to: string }): UseQueryResult<ITenantCreditStatement> {
  return useCompanyQuery({
    key: (orgId) => queryKeys.creditStatement(orgId, from, to),
    queryFn: (company) => fetchCreditStatement(company, from, to),
  })
}
